/**
 * InvoicingAddressSection — Billing step (Wave 4 native, resurrected from
 * b931d8142^).
 *
 * Shows saved invoicing addresses as selectable cards, with a "Create new"
 * affordance that opens the shared `InvoicingAddressPopup`. On create, the
 * new address is persisted via `useCreateInvoicingAddress(ForUser)` and
 * immediately selected — mirrors the legacy billing UX without mounting PF
 * contexts.
 *
 * Admin (targetUserId) path: addresses fetched via `useGetInvoicingAddressesByUserId`.
 * Client path: addresses fetched via `useGetInvoicingAddressesList`.
 *
 * Mapping: the slice's `AddressDTO` is lightweight — we denormalize the SF
 * id + label + inline fields so `serializeDraft` round-trips correctly. Only
 * a BE-persisted address (with `id`) ever reaches the slice — which is exactly
 * what `selectBillingStepValid` requires.
 *
 * Invoicing-address prefill. `GET /user/me/invoicing-address`
 * (`useGetInvoicingAddressesList`/`useGetInvoicingAddressesByUserId`) is the
 * per-user, structured source of truth; the v2 instruction-options
 * `billingAddress` string is structurally dead for workspace clients
 * (BE-confirmed — see `PurchaseStepInstructionV2DTO.billingAddress`) and was
 * never actually read here, so there is nothing upstream to "re-point" — this
 * section already sourced from the right endpoint.
 *
 * What WAS missing: nothing ever pre-selected a card, so a client with
 * exactly one saved address still had to click it before Billing's "Next"
 * gate (`selectBillingStepValid`, `store/selectors.ts`) would open. There is
 * no BE `isDefault` marker on `InvoicingAddressDTO` (unlike payment methods'
 * DF-B2 ladder), so picking one out of SEVERAL saved addresses would be a
 * guess the backend gives no basis for — out of scope. The single
 * unambiguous case is exactly one saved address: `effectiveValue` below
 * highlights it at RENDER time only (§11.1 — no setter call, so mount never
 * dirties the draft); a real click still runs the normal `onChange` path.
 *
 * KNOWN GAP (flagged, not fixed here — out of this task's file scope):
 * `selectBillingStepValidFor` (`store/selectors.ts`) and `useStepNav.ts`
 * (`components/cart/`) read `state.invoicingAddress` directly, so this
 * render-only highlight does NOT yet open the "Next" gate by itself — the
 * user must still click the highlighted card once, same as the DF-B2
 * payment-method preselect needed its own gate-threading step.
 *
 * VERIFIED 2026-08-13 (do not "fix" this by copying the DF-B2 threading
 * verbatim): a naive `defaultInvoicingAddress` thread into
 * `selectBillingStepValidFor`/`useStepNav.ts`, mirroring `defaultPaymentMethod`
 * exactly, would let a real `invoice`-method order ship with NO invoicing
 * address — `serializePlacement` (`store/serializePlacement.ts:329`)
 * optional-chains `state.invoicingAddress?.id` and its `compact()`
 * (`:78-84`) drops the key silently rather than throwing, and
 * `PlaceOrderClientV2DTO.invoicingAddressId` (`models/purchaseFlowRevamp/
 * placementV2DTO.ts:189`) is optional at the type level too — unlike
 * `paymentMethod`, which IS hard-guarded (`serializePlacement.ts:299`,
 * `dataQueries/purchaseFlowRevampPlaceOrder.query.ts:173-178`) so an
 * un-clicked default there blocks placement instead of silently omitting
 * data. Full rationale + the required fix order (guard first, then thread
 * the default) is on `selectBillingStepValidFor` in `store/selectors.ts`.
 */

import React, { useCallback, useMemo, useState } from 'react'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { GRAY_500 } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import type { AddressDTO } from 'models/purchaseFlowRevamp'
import {
  type InvoicingAddressDTO,
  useCreateInvoicingAddress,
  useCreateInvoicingAddressForUser,
  useGetInvoicingAddressesByUserId,
  useGetInvoicingAddressesList
} from 'dataQueries/invoicingAddresses.query'
import { InvoicingAddressPopup } from 'components/common/InvoicingAddressPopup'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
// Shared with `useDefaultInvoicingAddress`, so the highlight and
// the value the order is billed to are literally the same mapping.
import { dtoToSlice } from './invoicingAddressSlice'
import { useDefaultInvoicingAddress } from '../useDefaultInvoicingAddress'
import { PfInlineLoading } from '../../../components/PfInlineLoading.component'
import { PfRetryAlert } from '../../../components/PfRetryAlert.component'
import { toInvoicingAddressPayload, type InvoicingAddressFields } from 'components/forms/InvoicingAddress'
import { useAuth0 } from 'utils/auth'

import { MUICheckCard } from 'components/common/MUICards'

interface Props {
  value: AddressDTO | undefined
  onChange: (next: AddressDTO | undefined) => void
  /** Admin-flow: load addresses for the target client instead of the logged-in user. */
  targetUserId: string | null
}

/**
 * V-07: build the display line from the structured fields instead of trusting
 * the backend's precomposed `addressString` — the BE joins its own optional
 * segments (apartment/region) WITHOUT dropping the blanks, so a sparse address
 * renders literal double commas ("Ringstraße 1, , 4481 Asten, , AT"). The
 * backend sends `''` for an absent segment, not `null`/`undefined` (§13 trap 8),
 * so filter on trimmed truthiness rather than a plain nullish/falsy check.
 * Mirrors the legacy `InvoicingAddressCard`'s `.filter(Boolean).join(', ')` line.
 */
const formatAddressLine = (a: InvoicingAddressDTO): string =>
  [a.streetAndNumber, a.apartmentNumber, a.city, a.region, a.postalCode, a.country]
    .filter((segment) => !!segment?.trim())
    .join(', ')



export const InvoicingAddressSection: React.FC<Props> = ({ value, onChange, targetUserId }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const { roles } = useAuth0()

  const clientQuery = useGetInvoicingAddressesList(!targetUserId)
  const userScopedQuery = useGetInvoicingAddressesByUserId(targetUserId ?? undefined, !!targetUserId)
  const query = targetUserId ? userScopedQuery : clientQuery

  const createForClient = useCreateInvoicingAddress()
  const createForUser = useCreateInvoicingAddressForUser()

  const [popupOpen, setPopupOpen] = useState(false)

  const addresses = useMemo<InvoicingAddressDTO[]>(() => query.data ?? [], [query.data])

  // §11.1 — RENDER-TIME ONLY, no setter call: highlight the sole saved address
  // when nothing is selected yet. `value` (the real slice value) always wins
  // once anything has actually been chosen.
  //
  // The default now comes from the SAME hook the step gates and the
  // place-order body read, so the card shown as chosen is provably the one the
  // order is billed to. Deriving it separately here is what made the highlight
  // cosmetic and Proceed dead.
  const defaultInvoicingAddress = useDefaultInvoicingAddress()
  const effectiveValue = value ?? defaultInvoicingAddress

  const handleSelect = useCallback(
    (id: string) => {
      const match = addresses.find((a) => a.id === id)
      if (match) onChange(dtoToSlice(match))
    },
    [addresses, onChange]
  )

  const handleCreate = useCallback(
    (values: InvoicingAddressFields) => {
      const payload = toInvoicingAddressPayload(values)
      const done = (created: InvoicingAddressDTO) => {
        onChange(dtoToSlice(created))
        setPopupOpen(false)
      }

      if (targetUserId && roles.isAdmin) {
        createForUser.mutate(
          { ...payload, userId: targetUserId } as Parameters<typeof createForUser.mutate>[0],
          { onSuccess: (res) => done(res) }
        )
      } else {
        createForClient.mutate(payload, { onSuccess: (res) => done(res) })
      }
    },
    [createForClient, createForUser, onChange, roles.isAdmin, targetUserId]
  )

  return (
    <SectionedBorderBox variant="flat"
      title={t('purchase_flow_revamp:billing.invoicing_address.section_title')}
      description={t('purchase_flow_revamp:billing.invoicing_address.section_description')}
    >
      <Stack gap={1.2}>

        {query.isPending &&
          <PfInlineLoading message={t('purchase_flow_revamp:billing.invoicing_address.loading')} />
        }

        {query.isError &&
          <PfRetryAlert
            data-cy="pf-revamp-invoicing-address-error"
            message={t('purchase_flow_revamp:billing.invoicing_address.error')}
            onRetry={() => { query.refetch() }}
          />
        }

        {!query.isPending && !query.isError && addresses.length === 0 &&
          <Typography variant="text-sm" color={GRAY_500}>
            {t('purchase_flow_revamp:billing.invoicing_address.empty')}
          </Typography>
        }

        {!query.isPending && addresses.length > 0 &&
          <Stack gap={0.8} role="radiogroup" aria-label={t('purchase_flow_revamp:billing.invoicing_address.section_title')}>

            {addresses.map((a) => (
              <MUICheckCard
                key={a.id}
                isRadio
                checked={effectiveValue?.id === a.id}
                onCheck={() => handleSelect(a.id)}
                title={a.addressName}
                description={formatAddressLine(a)}
                data-cy={`pf-revamp-invoicing-address-${a.id}`}
              />
            ))}

          </Stack>
        }

        <MUIButton
          type="secondaryBorder"
          size="sm"
          onClick={() => setPopupOpen(true)}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('purchase_flow_revamp:billing.invoicing_address.add_new')}
        </MUIButton>

      </Stack>

      <InvoicingAddressPopup
        isOpen={popupOpen}
        mode="create"
        onClose={() => setPopupOpen(false)}
        onSubmit={handleCreate}
        query={(targetUserId ? createForUser : createForClient) as never}
      />

    </SectionedBorderBox>
  )
}
